import {
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  type FormEvent,
} from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ChevronLeft,
  ChevronRight,
  Columns3,
  Loader2,
  MoreHorizontal,
  Search,
  X,
} from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { cn } from '@/lib/utils';

/**
 * AdminDataTable — Issue #345 共享运营表格组件。
 *
 * 设计要点：
 * - 仅负责渲染、列可见性、搜索/筛选 UI 与翻页交互；不发起 API 请求。
 * - 由调用方使用 typed api-client 拉取数据后注入 `rows`，并把搜索/筛选条件
 *   通过 `filters.onSubmit` 透传到后端。前端不维护任何假业务状态。
 * - 列可见性默认是前端 UI 状态（per AI GUIDE §11，仅 UI 增强）。
 * - 行操作通过 `rowActions` 注入；调用方负责权限判断（visible/disabled），
 *   实际的写操作必须调用真实后端接口后再 refresh。
 */

export type AdminDataTableSortDirection = 'asc' | 'desc' | null;

export interface AdminDataTableColumn<Row> {
  /** Stable identifier — used for visibility/key. */
  key: string;
  header: string;
  render: (row: Row) => ReactNode;
  /** Width string passed to <th style>. */
  width?: string | number;
  /** When true the column cannot be hidden by the user. */
  alwaysVisible?: boolean;
  /** Hidden by default but selectable in the visibility menu. */
  defaultHidden?: boolean;
}

export interface AdminDataTableAction<Row> {
  /** Stable identifier for testing/aria. */
  key: string;
  label: string;
  /** Returns true when the action should be rendered for the given row. */
  visible?: (row: Row) => boolean;
  /** When true the action is rendered disabled (still visible for clarity). */
  disabled?: (row: Row) => boolean;
  /** When true the action is rendered with destructive styling. */
  destructive?: boolean;
  /** Optional icon rendered before the label. */
  icon?: ReactNode;
  onSelect: (row: Row) => void;
}

export type AdminDataTableFilterField =
  | {
      kind: 'text';
      name: string;
      label: string;
      placeholder?: string;
    }
  | {
      kind: 'select';
      name: string;
      label: string;
      options: ReadonlyArray<{ value: string; label: string }>;
      placeholder?: string;
    };

export interface AdminDataTableFilters {
  /** Field descriptors driving the toolbar. */
  fields: ReadonlyArray<AdminDataTableFilterField>;
  /** Current applied values (controlled by parent). */
  values: Record<string, string>;
  /** Submit handler — receives the just-edited form values. */
  onSubmit: (next: Record<string, string>) => void;
  /** Reset handler — clears the values and refetches. */
  onReset: () => void;
}

export interface AdminDataTablePagination {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (next: number) => void;
}

export interface AdminDataTableProps<Row extends { id: string | number }> {
  columns: ReadonlyArray<AdminDataTableColumn<Row>>;
  rows: ReadonlyArray<Row>;
  isLoading?: boolean;
  /** Pass a non-null error to render the error state. */
  error?: { message: string } | null;
  onRetry?: () => void;
  emptyMessage?: string;
  pagination?: AdminDataTablePagination;
  filters?: AdminDataTableFilters;
  rowActions?: ReadonlyArray<AdminDataTableAction<Row>>;
  /** Stable id used as a prefix for `data-testid` hooks. */
  testIdPrefix?: string;
  /** When true the column visibility menu is hidden. */
  hideColumnControls?: boolean;
  /** Extra toolbar nodes rendered next to the search bar (e.g. 新建按钮). */
  toolbarTrailing?: ReactNode;
}

function buildDefaultVisibility<Row>(
  columns: ReadonlyArray<AdminDataTableColumn<Row>>,
): Record<string, boolean> {
  const init: Record<string, boolean> = {};
  for (const c of columns) {
    init[c.key] = c.defaultHidden ? false : true;
  }
  return init;
}

function FilterField({
  field,
  value,
  onChange,
  testIdPrefix,
}: {
  field: AdminDataTableFilterField;
  value: string;
  onChange: (next: string) => void;
  testIdPrefix?: string;
}): ReactNode {
  const inputId = `${testIdPrefix ?? 'admin-table'}-filter-${field.name}`;
  if (field.kind === 'text') {
    return (
      <div className="flex min-w-[200px] flex-col gap-1">
        <label
          htmlFor={inputId}
          className="text-xs font-medium text-muted-foreground"
        >
          {field.label}
        </label>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id={inputId}
            type="text"
            value={value}
            placeholder={field.placeholder ?? `搜索${field.label}`}
            className="h-8 pl-7 text-sm"
            data-testid={`${testIdPrefix ?? 'admin-table'}-filter-${field.name}-input`}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      </div>
    );
  }
  return (
    <div className="flex min-w-[160px] flex-col gap-1">
      <label
        htmlFor={inputId}
        className="text-xs font-medium text-muted-foreground"
      >
        {field.label}
      </label>
      <select
        id={inputId}
        value={value}
        data-testid={`${testIdPrefix ?? 'admin-table'}-filter-${field.name}-select`}
        className="flex h-8 w-full rounded-md border border-input bg-transparent px-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{field.placeholder ?? '全部'}</option>
        {field.options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function AdminTableSearchBar({
  filters,
  testIdPrefix,
}: {
  filters: AdminDataTableFilters;
  testIdPrefix?: string;
}) {
  const [draft, setDraft] = useState<Record<string, string>>(filters.values);

  // Keep draft in sync if parent values change (e.g. external reset).
  useEffect(() => {
    setDraft(filters.values);
  }, [filters.values]);

  const updateField = (name: string, value: string) => {
    setDraft((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    filters.onSubmit(draft);
  };

  const onReset = () => {
    const cleared: Record<string, string> = {};
    for (const f of filters.fields) {
      cleared[f.name] = '';
    }
    setDraft(cleared);
    filters.onReset();
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid={`${testIdPrefix ?? 'admin-table'}-search-form`}
      className="flex flex-wrap items-end gap-3 rounded-md border bg-muted/20 p-3"
    >
      {filters.fields.map((field) => (
        <FilterField
          key={field.name}
          field={field}
          value={draft[field.name] ?? ''}
          onChange={(next) => updateField(field.name, next)}
          testIdPrefix={testIdPrefix}
        />
      ))}
      <div className="flex gap-2 pb-0.5">
        <Button
          type="submit"
          size="sm"
          data-testid={`${testIdPrefix ?? 'admin-table'}-search-submit`}
        >
          <Search className="h-3.5 w-3.5" />
          查询
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onReset}
          data-testid={`${testIdPrefix ?? 'admin-table'}-search-reset`}
        >
          <X className="h-3.5 w-3.5" />
          重置
        </Button>
      </div>
    </form>
  );
}

function ColumnVisibilityMenu<Row>({
  columns,
  visibility,
  onChange,
  testIdPrefix,
}: {
  columns: ReadonlyArray<AdminDataTableColumn<Row>>;
  visibility: Record<string, boolean>;
  onChange: (next: Record<string, boolean>) => void;
  testIdPrefix?: string;
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid={`${testIdPrefix ?? 'admin-table'}-columns-trigger`}
        >
          <Columns3 className="h-3.5 w-3.5" />
          列显示
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-50 min-w-[180px] rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
          data-testid={`${testIdPrefix ?? 'admin-table'}-columns-menu`}
        >
          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
            选择显示的列
          </div>
          {columns.map((col) => {
            const checked = visibility[col.key] !== false;
            const locked = col.alwaysVisible === true;
            return (
              <DropdownMenu.CheckboxItem
                key={col.key}
                checked={checked}
                disabled={locked}
                onCheckedChange={(next) => {
                  onChange({ ...visibility, [col.key]: Boolean(next) });
                }}
                onSelect={(e) => e.preventDefault()}
                data-testid={`${testIdPrefix ?? 'admin-table'}-column-toggle-${col.key}`}
                className={cn(
                  'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
                  'focus:bg-accent focus:text-accent-foreground',
                  locked ? 'opacity-60' : '',
                )}
              >
                <input
                  type="checkbox"
                  className="mr-2 h-3.5 w-3.5"
                  checked={checked}
                  readOnly
                  tabIndex={-1}
                  aria-hidden
                />
                <span>{col.header}</span>
              </DropdownMenu.CheckboxItem>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function RowActionMenu<Row extends { id: string | number }>({
  row,
  actions,
  testIdPrefix,
}: {
  row: Row;
  actions: ReadonlyArray<AdminDataTableAction<Row>>;
  testIdPrefix?: string;
}) {
  const visible = actions.filter((a) => (a.visible ? a.visible(row) : true));
  if (visible.length === 0) {
    return (
      <span className="text-xs text-muted-foreground">无可用操作</span>
    );
  }
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label="行操作"
          data-testid={`${testIdPrefix ?? 'admin-table'}-row-actions-${row.id}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-50 min-w-[140px] rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          {visible.map((action) => {
            const isDisabled = action.disabled ? action.disabled(row) : false;
            return (
              <DropdownMenu.Item
                key={action.key}
                disabled={isDisabled}
                onSelect={() => {
                  if (!isDisabled) action.onSelect(row);
                }}
                data-testid={`${testIdPrefix ?? 'admin-table'}-row-action-${action.key}-${row.id}`}
                className={cn(
                  'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
                  'focus:bg-accent focus:text-accent-foreground',
                  action.destructive ? 'text-destructive focus:text-destructive' : '',
                  isDisabled ? 'opacity-50' : '',
                )}
              >
                {action.icon ? <span className="mr-2">{action.icon}</span> : null}
                <span>{action.label}</span>
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function AdminDataTable<Row extends { id: string | number }>({
  columns,
  rows,
  isLoading = false,
  error = null,
  onRetry,
  emptyMessage = '暂无数据',
  pagination,
  filters,
  rowActions,
  testIdPrefix,
  hideColumnControls = false,
  toolbarTrailing,
}: AdminDataTableProps<Row>) {
  const [visibility, setVisibility] = useState<Record<string, boolean>>(() =>
    buildDefaultVisibility(columns),
  );

  // If the column set changes (e.g. permission-driven), reseed defaults for
  // any new keys without dropping the user's prior preferences for known keys.
  useEffect(() => {
    setVisibility((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const c of columns) {
        if (!(c.key in next)) {
          next[c.key] = c.defaultHidden ? false : true;
          changed = true;
        }
      }
      for (const key of Object.keys(prev)) {
        if (!columns.some((c) => c.key === key)) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [columns]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => visibility[c.key] !== false),
    [columns, visibility],
  );

  const showRowActions = Boolean(rowActions && rowActions.length > 0);
  const totalCols = visibleColumns.length + (showRowActions ? 1 : 0);

  return (
    <div className="space-y-3" data-testid={testIdPrefix ?? 'admin-table'}>
      {(filters || !hideColumnControls || toolbarTrailing) && (
        <div className="flex flex-col gap-3">
          {filters ? (
            <AdminTableSearchBar filters={filters} testIdPrefix={testIdPrefix} />
          ) : null}
          {(!hideColumnControls || toolbarTrailing) && (
            <div className="flex items-center justify-between">
              <div>{toolbarTrailing}</div>
              {!hideColumnControls ? (
                <ColumnVisibilityMenu
                  columns={columns}
                  visibility={visibility}
                  onChange={setVisibility}
                  testIdPrefix={testIdPrefix}
                />
              ) : null}
            </div>
          )}
        </div>
      )}

      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {visibleColumns.map((c) => (
                <TableHead
                  key={c.key}
                  style={c.width ? { width: c.width } : undefined}
                >
                  {c.header}
                </TableHead>
              ))}
              {showRowActions ? (
                <TableHead className="w-[80px] text-right">操作</TableHead>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={totalCols} className="py-10 text-center">
                  <div
                    className="flex items-center justify-center gap-2 text-sm text-muted-foreground"
                    data-testid={`${testIdPrefix ?? 'admin-table'}-loading`}
                  >
                    <Loader2 className="h-4 w-4 animate-spin" />
                    加载中…
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={totalCols} className="py-10 text-center">
                  <div
                    className="flex flex-col items-center gap-2 text-sm text-destructive"
                    data-testid={`${testIdPrefix ?? 'admin-table'}-error`}
                  >
                    <span>加载失败：{error.message}</span>
                    {onRetry ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onRetry}
                        data-testid={`${testIdPrefix ?? 'admin-table'}-retry`}
                      >
                        重试
                      </Button>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={totalCols} className="py-10 text-center">
                  <div
                    className="text-sm text-muted-foreground"
                    data-testid={`${testIdPrefix ?? 'admin-table'}-empty`}
                  >
                    {emptyMessage}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-testid={`${testIdPrefix ?? 'admin-table'}-row-${row.id}`}
                >
                  {visibleColumns.map((c) => (
                    <TableCell key={c.key}>{c.render(row)}</TableCell>
                  ))}
                  {showRowActions ? (
                    <TableCell className="text-right">
                      <RowActionMenu
                        row={row}
                        actions={rowActions ?? []}
                        testIdPrefix={testIdPrefix}
                      />
                    </TableCell>
                  ) : null}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {pagination ? (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span data-testid={`${testIdPrefix ?? 'admin-table'}-pagination-summary`}>
            第 {pagination.page} 页 · 每页 {pagination.pageSize} 条 · 共{' '}
            {pagination.total} 条
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => pagination.onPageChange(pagination.page - 1)}
              aria-label="上一页"
              data-testid={`${testIdPrefix ?? 'admin-table'}-prev`}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={
                pagination.page * pagination.pageSize >= pagination.total
              }
              onClick={() => pagination.onPageChange(pagination.page + 1)}
              aria-label="下一页"
              data-testid={`${testIdPrefix ?? 'admin-table'}-next`}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
